import type { KeyResult, Objective, Project, Task } from '@portfolio-pm/shared';
import type { ReactNode } from 'react';
import { ScrollViewport } from '../layout/ScrollViewport.tsx';
import { matchesTaskSearch, parseWorkspaceSearch } from '../search/workspace-search.ts';
import { StatusBadge } from './okr-ui.tsx';

/**
 * Hierarchy grid display for the OKR workspace (REQ-OKR-005).
 * Extracted from OkrWorkspace.tsx.
 */

export interface OkrGridRow {
  id: string;
  project: Project | undefined;
  objectivePath: Objective[];
  keyResults: KeyResult[];
  linkedTasks: Task[];
}

export function OkrGridView({
  rows,
  searchActive,
  getObjectiveProgress,
  getKrProgress,
  onSelectObjective,
  onSelectKr,
  onOpenTask,
}: {
  rows: OkrGridRow[];
  searchActive: boolean;
  getObjectiveProgress(id: string): number;
  getKrProgress(id: string): number;
  onSelectObjective(objectiveId: string): void;
  onSelectKr(keyResultId: string): void;
  onOpenTask: ((taskId: string) => void) | undefined;
}): JSX.Element {
  const maxDepth = Math.max(1, ...rows.map((row) => row.objectivePath.length));

  if (rows.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <div className="surface-empty max-w-lg text-center">
          {searchActive
            ? 'No OKRs, KRs, or linked tasks match this search.'
            : 'No objectives yet for this project.'}
        </div>
      </div>
    );
  }

  const levelColumns = Array.from({ length: maxDepth }, (_, index) => ({
    id: `okr-grid-level-${index + 1}`,
    label: `Level ${index + 1}`,
  }));

  return (
    <ScrollViewport
      data-testid="okr-grid-view"
      className="h-full min-h-0"
      viewportClassName="h-full"
      contentClassName="min-w-max p-3"
    >
      <div
        className="grid gap-2"
        style={{
          gridTemplateColumns: `10rem repeat(${maxDepth}, minmax(11rem, 1fr)) minmax(15rem, 1.2fr) minmax(15rem, 1.2fr)`,
        }}
      >
        <GridHeading>Project</GridHeading>
        {levelColumns.map((column) => (
          <GridHeading key={column.id}>{column.label}</GridHeading>
        ))}
        <GridHeading>Key results</GridHeading>
        <GridHeading>Linked tasks</GridHeading>

        {rows.map((row) => (
          <OkrGridRowView
            key={row.id}
            row={row}
            maxDepth={maxDepth}
            getObjectiveProgress={getObjectiveProgress}
            getKrProgress={getKrProgress}
            onSelectObjective={onSelectObjective}
            onSelectKr={onSelectKr}
            onOpenTask={onOpenTask}
          />
        ))}
      </div>
    </ScrollViewport>
  );
}

function OkrGridRowView({
  row,
  maxDepth,
  getObjectiveProgress,
  getKrProgress,
  onSelectObjective,
  onSelectKr,
  onOpenTask,
}: {
  row: OkrGridRow;
  maxDepth: number;
  getObjectiveProgress(id: string): number;
  getKrProgress(id: string): number;
  onSelectObjective(objectiveId: string): void;
  onSelectKr(keyResultId: string): void;
  onOpenTask: ((taskId: string) => void) | undefined;
}): JSX.Element {
  return (
    <>
      <div
        data-testid="okr-grid-row"
        className="rounded-md border border-[color:var(--app-border)] bg-white/70 p-2 text-sm font-medium text-[color:var(--app-text)]"
      >
        <span className="line-clamp-2">{row.project?.name ?? 'Project'}</span>
      </div>
      {Array.from({ length: maxDepth }, (_, index) => {
        const objective = row.objectivePath[index];
        return objective ? (
          <button
            key={objective.id}
            type="button"
            onClick={() => onSelectObjective(objective.id)}
            className="rounded-md border border-[color:var(--app-border)] bg-white/70 p-2 text-left transition hover:border-[color:var(--app-border-strong)] hover:bg-white"
          >
            <div className="flex items-center justify-between gap-2">
              <StatusBadge status={objective.status} />
              <span className="font-mono text-[11px] text-[color:var(--app-text-soft)]">
                {Math.round(getObjectiveProgress(objective.id))}%
              </span>
            </div>
            <p className="mt-1 line-clamp-3 text-sm font-medium text-[color:var(--app-text)]">
              {objective.title}
            </p>
          </button>
        ) : (
          <div
            key={`blank-${row.id}-${index}`}
            className="rounded-md border border-dashed border-[color:var(--app-border)] bg-white/30"
          />
        );
      })}
      <div className="space-y-1 rounded-md border border-[color:var(--app-border)] bg-white/70 p-2">
        {row.keyResults.length === 0 ? (
          <p className="text-xs text-[color:var(--app-text-soft)]">No KRs</p>
        ) : (
          row.keyResults.map((kr) => (
            <button
              key={kr.id}
              type="button"
              onClick={() => onSelectKr(kr.id)}
              className="w-full rounded px-2 py-1 text-left transition hover:bg-[color:var(--app-info-soft)]"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="line-clamp-2 text-xs font-medium text-[color:var(--app-text)]">
                  {kr.title}
                </span>
                <span className="shrink-0 font-mono text-[11px] text-[color:var(--app-text-soft)]">
                  {Math.round(getKrProgress(kr.id))}%
                </span>
              </div>
            </button>
          ))
        )}
      </div>
      <div className="space-y-1 rounded-md border border-[color:var(--app-border)] bg-white/70 p-2">
        {row.linkedTasks.length === 0 ? (
          <p className="text-xs text-[color:var(--app-text-soft)]">No linked tasks</p>
        ) : (
          row.linkedTasks.map((task) => (
            <button
              key={task.id}
              type="button"
              onClick={() => onOpenTask?.(task.id)}
              className={`w-full rounded px-2 py-1 text-left text-xs transition hover:bg-[color:var(--app-accent-soft)] ${
                task.completedAt !== undefined
                  ? 'text-[color:var(--app-text-soft)] line-through'
                  : 'text-[color:var(--app-text-muted)]'
              }`}
            >
              {task.title}
            </button>
          ))
        )}
      </div>
    </>
  );
}

function GridHeading({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="sticky top-0 z-10 rounded-md border border-[color:var(--app-border)] bg-[color:var(--app-mobile-bar-bg)] px-2 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[color:var(--app-text-soft)] backdrop-blur">
      {children}
    </div>
  );
}

export function buildOkrGridRows({
  objectives,
  allObjectives,
  keyResults,
  tasksByKrId,
  projectsById,
  searchQuery,
  now,
}: {
  objectives: Objective[];
  allObjectives: Objective[];
  keyResults: KeyResult[];
  tasksByKrId: Map<string, Task[]>;
  projectsById: Map<string, Project>;
  searchQuery: string;
  now: number;
}): OkrGridRow[] {
  const objectiveById = new Map(allObjectives.map((objective) => [objective.id, objective]));
  const keyResultsByObjective = new Map<string, KeyResult[]>();
  for (const kr of keyResults) {
    const objective = objectiveById.get(kr.objectiveId);
    if (!objective) continue;
    const list = keyResultsByObjective.get(kr.objectiveId) ?? [];
    list.push(kr);
    keyResultsByObjective.set(kr.objectiveId, list);
  }
  for (const krs of keyResultsByObjective.values()) krs.sort((a, b) => a.order - b.order);

  const parsed = parseWorkspaceSearch(searchQuery);
  const searchActive = parsed.mode === 'search';

  return objectives
    .slice()
    .sort((left, right) => {
      if (left.projectId !== right.projectId) {
        return (
          (projectsById.get(left.projectId)?.order ?? 0) -
          (projectsById.get(right.projectId)?.order ?? 0)
        );
      }
      return left.order - right.order;
    })
    .map((objective) => {
      const objectiveKrs = keyResultsByObjective.get(objective.id) ?? [];
      const linkedTasks = objectiveKrs.flatMap((kr) => tasksByKrId.get(kr.id) ?? []);
      return {
        id: objective.id,
        project: projectsById.get(objective.projectId),
        objectivePath: buildObjectivePath(objective, objectiveById),
        keyResults: objectiveKrs,
        linkedTasks,
      };
    })
    .filter((row) => {
      if (!searchActive) return true;
      if (
        matchesOkrTitle(
          row.objectivePath.map((objective) => objective.title),
          searchQuery,
        )
      ) {
        return true;
      }
      if (
        matchesOkrTitle(
          row.keyResults.map((kr) => kr.title),
          searchQuery,
        )
      )
        return true;
      return row.linkedTasks.some((task) => matchesTaskSearch(task, searchQuery, now));
    });
}

function buildObjectivePath(
  objective: Objective,
  objectiveById: Map<string, Objective>,
): Objective[] {
  const path: Objective[] = [];
  const visited = new Set<string>();
  let cursor: Objective | undefined = objective;

  while (cursor && !visited.has(cursor.id)) {
    visited.add(cursor.id);
    path.unshift(cursor);
    cursor = cursor.parentObjectiveId ? objectiveById.get(cursor.parentObjectiveId) : undefined;
  }

  return path;
}

function matchesOkrTitle(titles: string[], query: string): boolean {
  const parsed = parseWorkspaceSearch(query);
  if (parsed.mode !== 'search' || parsed.filters.length > 0 || parsed.terms.length === 0) {
    return false;
  }
  return titles.some((title) =>
    parsed.terms.every((term) => title.toLowerCase().includes(term.toLowerCase())),
  );
}
